import { getCache, ServiceType } from './indexeddb-cache';
import { readAdminCredentialRevision } from './storage/admin-credential-revision';
import type { CachedUsageDetails, OrganizationData } from './openai-api/types';

const credentialScopedParams = (
  params: Record<string, unknown>,
  credentialRevision: string = readAdminCredentialRevision()
) => ({ ...params, credentialRevision });

/**
 * OpenAI Cache Service
 */
export class OpenAICacheService {
  private cache = getCache();
  private service: ServiceType = 'openai';

  /**
   * Cache organization usage details
   */
  async cacheOrganizationUsage(
    startDate: string,
    endDate: string,
    data: unknown,
    customTTL?: number,
    credentialRevision?: string
  ): Promise<boolean> {
    const params = credentialScopedParams({ startDate, endDate }, credentialRevision);
    return this.cache.set(this.service, 'organization_usage_details', data, params, customTTL);
  }

  /**
   * Get cached organization usage details
   */
  async getOrganizationUsage(
    startDate: string,
    endDate: string,
    credentialRevision?: string
  ): Promise<CachedUsageDetails | null> {
    const params = credentialScopedParams({ startDate, endDate }, credentialRevision);
    return this.cache.get(this.service, 'organization_usage_details', params);
  }

  /**
   * Cache projects data
   */
  async cacheProjects(data: unknown, customTTL?: number): Promise<boolean> {
    return this.cache.set(this.service, 'projects', data, credentialScopedParams({}), customTTL);
  }

  /**
   * Get cached projects data
   */
  async getProjects(): Promise<unknown | null> {
    return this.cache.get(this.service, 'projects', credentialScopedParams({}));
  }

  /**
   * Cache users data
   */
  async cacheUsers(data: unknown, customTTL?: number): Promise<boolean> {
    return this.cache.set(this.service, 'users', data, credentialScopedParams({}), customTTL);
  }

  /**
   * Get cached users data
   */
  async getUsers(): Promise<unknown | null> {
    return this.cache.get(this.service, 'users', credentialScopedParams({}));
  }

  /**
   * Cache organization data
   */
  async cacheOrganizationData(data: unknown, customTTL?: number): Promise<boolean> {
    return this.cache.set(this.service, 'organization_data', data, credentialScopedParams({}), customTTL);
  }

  /**
   * Get cached organization data
   */
  async getOrganizationData(): Promise<OrganizationData | null> {
    return this.cache.get(this.service, 'organization_data', credentialScopedParams({}));
  }

  /**
   * Clear specific organization usage cache
   */
  async clearOrganizationUsageCache(
    startDate: string,
    endDate: string,
    credentialRevision?: string
  ): Promise<boolean> {
    const params = credentialScopedParams({ startDate, endDate }, credentialRevision);
    return this.cache.delete(this.service, 'organization_usage_details', params);
  }

  /**
   * Cache raw usage segment data for incremental range fetching
   */
  async cacheUsageSegment(
    bucketWidth: '1h' | '1d' | '1m',
    startTime: number,
    endTime: number,
    data: unknown,
    customTTL?: number | null,
    credentialRevision?: string
  ): Promise<boolean> {
    const params = credentialScopedParams({ bucketWidth, startTime, endTime }, credentialRevision);
    return this.cache.set(this.service, 'usage_segment_v1', data, params, customTTL);
  }

  /**
   * Remove a specific usage segment entry.
   */
  async removeUsageSegment(
    bucketWidth: '1h' | '1d' | '1m',
    startTime: number,
    endTime: number,
    credentialRevision?: string
  ): Promise<boolean> {
    const params = credentialScopedParams({ bucketWidth, startTime, endTime }, credentialRevision);
    return this.cache.delete(this.service, 'usage_segment_v1', params);
  }

  /**
   * Get cached usage segments that overlap with the target range
   */
  async getUsageSegments(
    bucketWidth: '1h' | '1d' | '1m',
    startTime: number,
    endTime: number,
    credentialRevision: string = readAdminCredentialRevision()
  ): Promise<Array<{ startTime: number; endTime: number; data: unknown }>> {
    const maxSpanSeconds = bucketWidth === '1d' ? 31 * 86400 : bucketWidth === '1h' ? 168 * 3600 : 1440 * 60;
    const entries = await this.cache.listRange(this.service, 'usage_segment_v1', credentialRevision,
      bucketWidth, startTime, endTime, maxSpanSeconds);
    return entries
      .map(entry => ({
        startTime: Number(entry.params?.startTime),
        endTime: Number(entry.params?.endTime),
        bucketWidth: entry.params?.bucketWidth,
        credentialRevision: entry.params?.credentialRevision,
        data: entry.data
      }))
      .filter(entry => (
        entry.bucketWidth === bucketWidth &&
        entry.credentialRevision === credentialRevision &&
        Number.isFinite(entry.startTime) &&
        Number.isFinite(entry.endTime) &&
        entry.endTime > startTime &&
        entry.startTime < endTime
      ))
      .map(({ startTime: rangeStart, endTime: rangeEnd, data }) => ({
        startTime: rangeStart,
        endTime: rangeEnd,
        data
      }));
  }

  /**
   * Clear all OpenAI cache
   */
  async clearCache(): Promise<number> {
    return this.cache.clearService(this.service);
  }
}

/**
 * Anthropic Cache Service
 */
export class AnthropicCacheService {
  private cache = getCache();
  private service: ServiceType = 'anthropic';

  /**
   * Cache usage data
   */
  async cacheUsage(
    startDate: string,
    endDate: string,
    data: unknown,
    customTTL?: number,
    credentialRevision?: string
  ): Promise<boolean> {
    const params = credentialScopedParams({ startDate, endDate }, credentialRevision);
    return this.cache.set(this.service, 'usage', data, params, customTTL);
  }

  /**
   * Get cached usage data
   */
  async getUsage(
    startDate: string,
    endDate: string,
    credentialRevision?: string
  ): Promise<unknown | null> {
    const params = credentialScopedParams({ startDate, endDate }, credentialRevision);
    return this.cache.get(this.service, 'usage', params);
  }

  /**
   * Cache raw usage segment data for incremental range fetching
   */
  async cacheUsageSegment(
    bucketWidth: '1h' | '1d' | '1m',
    startTime: number,
    endTime: number,
    data: unknown,
    customTTL?: number,
    credentialRevision?: string
  ): Promise<boolean> {
    const params = credentialScopedParams({ bucketWidth, startTime, endTime }, credentialRevision);
    return this.cache.set(this.service, 'usage_segment_v1', data, params, customTTL);
  }

  /**
   * Remove a specific usage segment entry.
   */
  async removeUsageSegment(
    bucketWidth: '1h' | '1d' | '1m',
    startTime: number,
    endTime: number,
    credentialRevision?: string
  ): Promise<boolean> {
    const params = credentialScopedParams({ bucketWidth, startTime, endTime }, credentialRevision);
    return this.cache.delete(this.service, 'usage_segment_v1', params);
  }

  /**
   * Get cached usage segments that overlap with the target range
   */
  async getUsageSegments(
    bucketWidth: '1h' | '1d' | '1m',
    startTime: number,
    endTime: number,
    credentialRevision: string = readAdminCredentialRevision()
  ): Promise<Array<{ startTime: number; endTime: number; data: unknown }>> {
    const entries = await this.cache.list(this.service, 'usage_segment_v1');
    return entries
      .map(entry => ({
        startTime: Number(entry.params?.startTime),
        endTime: Number(entry.params?.endTime),
        bucketWidth: entry.params?.bucketWidth,
        credentialRevision: entry.params?.credentialRevision,
        data: entry.data
      }))
      .filter(entry => (
        entry.bucketWidth === bucketWidth &&
        entry.credentialRevision === credentialRevision &&
        Number.isFinite(entry.startTime) &&
        Number.isFinite(entry.endTime) &&
        entry.endTime > startTime &&
        entry.startTime < endTime
      ))
      .map(({ startTime: rangeStart, endTime: rangeEnd, data }) => ({
        startTime: rangeStart,
        endTime: rangeEnd,
        data
      }));
  }

  /**
   * Cache credits data
   */
  async cacheCredits(data: unknown, customTTL?: number): Promise<boolean> {
    return this.cache.set(this.service, 'credits', data, credentialScopedParams({}), customTTL);
  }

  /**
   * Get cached credits data
   */
  async getCredits(): Promise<unknown | null> {
    return this.cache.get(this.service, 'credits', credentialScopedParams({}));
  }

  /**
   * Clear all Anthropic cache
   */
  async clearCache(): Promise<number> {
    return this.cache.clearService(this.service);
  }
}

/**
 * Gemini Cache Service
 */
export class GeminiCacheService {
  private cache = getCache();
  private service: ServiceType = 'gemini';

  /**
   * Cache usage data
   */
  async cacheUsage(
    startDate: string,
    endDate: string,
    data: unknown,
    customTTL?: number
  ): Promise<boolean> {
    const params = { startDate, endDate };
    return this.cache.set(this.service, 'usage', data, params, customTTL);
  }

  /**
   * Get cached usage data
   */
  async getUsage(startDate: string, endDate: string): Promise<unknown | null> {
    const params = { startDate, endDate };
    return this.cache.get(this.service, 'usage', params);
  }

  /**
   * Cache quota data
   */
  async cacheQuota(data: unknown, customTTL?: number): Promise<boolean> {
    return this.cache.set(this.service, 'quota', data, {}, customTTL);
  }

  /**
   * Get cached quota data
   */
  async getQuota(): Promise<unknown | null> {
    return this.cache.get(this.service, 'quota', {});
  }

  /**
   * Cache models data
   */
  async cacheModels(data: unknown, customTTL?: number): Promise<boolean> {
    return this.cache.set(this.service, 'models', data, {}, customTTL);
  }

  /**
   * Get cached models data
   */
  async getModels(): Promise<unknown | null> {
    return this.cache.get(this.service, 'models', {});
  }

  /**
   * Clear all Gemini cache
   */
  async clearCache(): Promise<number> {
    return this.cache.clearService(this.service);
  }
}

/**
 * GitHub Cache Service
 */
export class GitHubCacheService {
  private cache = getCache();
  private service: ServiceType = 'github';

  /**
   * Cache rate limit data
   */
  async cacheRateLimit(data: unknown, customTTL?: number): Promise<boolean> {
    return this.cache.set(this.service, 'rate_limit', data, {}, customTTL);
  }

  /**
   * Get cached rate limit data
   */
  async getRateLimit(): Promise<unknown | null> {
    return this.cache.get(this.service, 'rate_limit', {});
  }

  /**
   * Cache API usage data
   */
  async cacheApiUsage(
    startDate: string,
    endDate: string,
    data: unknown,
    customTTL?: number
  ): Promise<boolean> {
    const params = { startDate, endDate };
    return this.cache.set(this.service, 'api_usage', data, params, customTTL);
  }

  /**
   * Get cached API usage data
   */
  async getApiUsage(startDate: string, endDate: string): Promise<unknown | null> {
    const params = { startDate, endDate };
    return this.cache.get(this.service, 'api_usage', params);
  }

  /**
   * Cache actions usage data
   */
  async cacheActionsUsage(
    startDate: string,
    endDate: string,
    data: unknown,
    customTTL?: number
  ): Promise<boolean> {
    const params = { startDate, endDate };
    return this.cache.set(this.service, 'actions_usage', data, params, customTTL);
  }

  /**
   * Get cached actions usage data
   */
  async getActionsUsage(startDate: string, endDate: string): Promise<unknown | null> {
    const params = { startDate, endDate };
    return this.cache.get(this.service, 'actions_usage', params);
  }

  /**
   * Clear all GitHub cache
   */
  async clearCache(): Promise<number> {
    return this.cache.clearService(this.service);
  }
}

/**
 * Unified cache management
 */
export class CacheManager {
  private cache = getCache();
  
  // Service instances
  public openai = new OpenAICacheService();
  public anthropic = new AnthropicCacheService();
  public gemini = new GeminiCacheService();
  public github = new GitHubCacheService();

  /**
   * Clear expired cache entries across all services
   */
  async clearExpired(): Promise<number> {
    return this.cache.clearExpired();
  }

  /**
   * Clear all cache entries
   */
  async clearAll(): Promise<number> {
    return this.cache.clearAll();
  }

  /**
   * Get cache statistics for all services
   */
  async getStats() {
    return this.cache.getStats();
  }

  /**
   * Close cache connection
   */
  close(): void {
    this.cache.close();
  }
}

// Singleton instance
let cacheManagerInstance: CacheManager | null = null;

/**
 * Get singleton cache manager instance
 */
export const getCacheManager = (): CacheManager => {
  if (!cacheManagerInstance) {
    cacheManagerInstance = new CacheManager();
  }
  return cacheManagerInstance;
};

// Export individual service instances for convenience
export const openaiCache = getCacheManager().openai;
export const anthropicCache = getCacheManager().anthropic;
export const geminiCache = getCacheManager().gemini;
export const githubCache = getCacheManager().github;
